import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

interface InvestorSelectProps {
  investors: Array<{
    id: string;
    name: string;
    email: string;
    phone?: string;
    status?: string;
  }>;
  value: string;
  onChange: (investorId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
};

export const InvestorSelect: React.FC<InvestorSelectProps> = ({
  investors,
  value,
  onChange,
  placeholder = 'Seleccionar inversionista…',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedInvestor = investors.find((inv) => inv.id === value);

  const filteredInvestors = investors.filter((inv) => {
    const q = searchTerm.toLowerCase();
    return (
      inv.name.toLowerCase().includes(q) ||
      inv.email.toLowerCase().includes(q) ||
      (inv.phone && inv.phone.includes(q))
    );
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (investorId: string) => {
    onChange(investorId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:bg-gray-50'
        } ${selectedInvestor ? 'pr-8' : ''}`}
      >
        <span className={selectedInvestor ? 'text-gray-900 truncate' : 'text-gray-400'}>
          {selectedInvestor ? selectedInvestor.name : placeholder}
        </span>
        <div className="flex items-center space-x-1.5 flex-shrink-0 ml-2">
          {selectedInvestor && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-gray-200 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar inversionista…"
                className="w-full pl-9 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-52">
            {filteredInvestors.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">
                No se encontraron inversionistas
              </div>
            ) : (
              filteredInvestors.map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => handleSelect(inv.id)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    value === inv.id ? 'bg-primary/5 border-l-[3px] border-l-primary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {inv.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {inv.email}
                      </p>
                      {inv.phone && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {inv.phone}
                        </p>
                      )}
                    </div>
                    {inv.status && (
                      <span
                        className={`flex-shrink-0 inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          inv.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {STATUS_LABEL[inv.status] ?? inv.status}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
